/**
 * Helpers - General-purpose utility functions
 * 
 * Features:
 * - Debounce and throttle utilities
 * - Object deep comparison functions
 * - Array manipulation helpers
 * - String formatting utilities
 * - Browser detection and feature checking
 * - Performance measurement utilities
 */

/**
 * Helpers class with comprehensive utility functions
 */
class Helpers {
  /**
   * Debounce function execution
   * Delays function execution until after a specified wait time has elapsed
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @param {boolean} immediate - Whether to call immediately on first invocation
   * @returns {Function} Debounced function
   */
  static debounce(func, wait, immediate = false) {
    if (typeof func !== 'function') {
      throw new TypeError('Expected a function');
    }

    let timeout;
    
    return function executedFunction(...args) {
      const context = this;
      
      const later = () => {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      
      if (callNow) func.apply(context, args);
    };
  }

  /**
   * Throttle function execution
   * Limits function execution to at most once per specified time period
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @param {Object} options - Throttle options
   * @param {boolean} options.leading - Execute on leading edge (default: true)
   * @param {boolean} options.trailing - Execute on trailing edge (default: true)
   * @returns {Function} Throttled function
   */
  static throttle(func, limit, options = {}) {
    if (typeof func !== 'function') {
      throw new TypeError('Expected a function');
    }

    const { leading = true, trailing = true } = options;
    let inThrottle;
    let lastResult;
    let lastArgs;
    let timeout;

    return function throttledFunction(...args) {
      const context = this;

      if (!inThrottle) {
        if (leading) {
          lastResult = func.apply(context, args);
        } else {
          lastArgs = args;
        }

        inThrottle = true;
        
        setTimeout(() => {
          inThrottle = false;
          
          if (trailing && lastArgs) {
            lastResult = func.apply(context, lastArgs);
            lastArgs = null;
          }
        }, limit);
      } else if (trailing) {
        lastArgs = args;
      }

      return lastResult;
    };
  }

  /**
   * Deep comparison of two objects
   * @param {any} a - First value
   * @param {any} b - Second value
   * @returns {boolean} True if deeply equal
   */
  static deepEqual(a, b) {
    if (a === b) return true;

    if (a === null || a === undefined || b === null || b === undefined) return false;
    if (typeof a !== 'object' || typeof b !== 'object') return false;

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!this.deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

  /**
   * Deep clone an object
   * @param {any} obj - Object to clone
   * @returns {any} Cloned object
   */
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof RegExp) return new RegExp(obj);
    if (obj instanceof Array) return obj.map(item => this.deepClone(item));
    if (typeof obj === 'object') {
      const cloned = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          cloned[key] = this.deepClone(obj[key]);
        }
      }
      return cloned;
    }
    return obj;
  }

  /**
   * Merge objects deeply
   * @param {...Object} objects - Objects to merge
   * @returns {Object} Merged object
   */
  static deepMerge(...objects) {
    const result = {};
    
    for (const obj of objects) {
      if (!obj || typeof obj !== 'object') continue;
      
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          if (
            typeof obj[key] === 'object' &&
            obj[key] !== null &&
            !Array.isArray(obj[key]) &&
            !(obj[key] instanceof Date) &&
            typeof result[key] === 'object' &&
            result[key] !== null &&
            !Array.isArray(result[key]) &&
            !(result[key] instanceof Date)
          ) {
            result[key] = this.deepMerge(result[key], obj[key]);
          } else {
            result[key] = this.deepClone(obj[key]);
          }
        }
      }
    }
    
    return result;
  }

  /**
   * Get nested property from object
   * @param {Object} obj - Object to get property from
   * @param {string} path - Property path (e.g., 'user.profile.name')
   * @param {any} defaultValue - Default value if property doesn't exist
   * @returns {any} Property value or defaultValue
   */
  static getNestedProperty(obj, path, defaultValue = undefined) {
    if (!obj || typeof obj !== 'object') return defaultValue;
    if (!path || typeof path !== 'string') return defaultValue;

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return defaultValue;
      }
      current = current[key];
      if (current === undefined) {
        return defaultValue;
      }
    }

    return current;
  }

  /**
   * Set nested property in object
   * @param {Object} obj - Object to set property in
   * @param {string} path - Property path (e.g., 'user.profile.name')
   * @param {any} value - Value to set
   * @returns {Object} Modified object
   */
  static setNestedProperty(obj, path, value) {
    if (!obj || typeof obj !== 'object') return obj;
    if (!path || typeof path !== 'string') return obj;

    const keys = path.split('.');
    const lastKey = keys.pop();
    let current = obj;

    for (const key of keys) {
      if (current[key] === null || current[key] === undefined || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[lastKey] = value;
    return obj;
  }

  /**
   * Remove duplicates from array
   * @param {Array} array - Array to deduplicate
   * @param {Function|string} keyFunc - Optional function or key to extract comparison key
   * @returns {Array} Deduplicated array
   */
  static removeDuplicates(array, keyFunc = null) {
    if (!Array.isArray(array)) return [];

    if (!keyFunc) {
      return Array.from(new Set(array));
    }

    const seen = new Set();
    const isFunction = typeof keyFunc === 'function';
    const isString = typeof keyFunc === 'string';

    return array.filter(item => {
      let key;
      if (isFunction) {
        key = keyFunc(item);
      } else if (isString) {
        key = item[keyFunc];
      } else {
        key = JSON.stringify(item);
      }

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  /**
   * Group array by key
   * @param {Array} array - Array to group
   * @param {Function|string} keyFunc - Function or key to extract grouping key
   * @returns {Object} Grouped object
   */
  static groupBy(array, keyFunc) {
    if (!Array.isArray(array)) return {};

    const isFunction = typeof keyFunc === 'function';
    const isString = typeof keyFunc === 'string';
    const result = {};

    for (const item of array) {
      let key;
      if (isFunction) {
        key = keyFunc(item);
      } else if (isString) {
        key = item[keyFunc];
      } else {
        key = String(keyFunc);
      }

      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
    }

    return result;
  }

  /**
   * Sort array by multiple criteria
   * @param {Array} array - Array to sort
   * @param {Array} sortCriteria - Array of sort criteria objects
   * @param {string} sortCriteria.field - Field to sort by
   * @param {string} sortCriteria.direction - Sort direction ('asc' or 'desc')
   * @returns {Array} Sorted array
   */
  static multiSort(array, sortCriteria) {
    if (!Array.isArray(array) || !Array.isArray(sortCriteria)) {
      return array;
    }

    return array.slice().sort((a, b) => {
      for (const criteria of sortCriteria) {
        const aValue = this.getNestedProperty(a, criteria.field);
        const bValue = this.getNestedProperty(b, criteria.field);

        let comparison = 0;
        if (aValue < bValue) comparison = -1;
        else if (aValue > bValue) comparison = 1;

        if (criteria.direction === 'desc') {
          comparison = -comparison;
        }

        if (comparison !== 0) return comparison;
      }

      return 0;
    });
  }

  /**
   * Chunk array into smaller arrays
   * @param {Array} array - Array to chunk
   * @param {number} size - Chunk size
   * @returns {Array} Array of chunks
   */
  static chunk(array, size) {
    if (!Array.isArray(array) || size <= 0) return [];
    
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  /**
   * Flatten nested array
   * @param {Array} array - Array to flatten
   * @param {number} depth - Flattening depth (default: Infinity)
   * @returns {Array} Flattened array
   */
  static flatten(array, depth = Infinity) {
    if (!Array.isArray(array)) return [];
    
    if (depth === 0) return array.slice();
    
    return array.reduce((acc, item) => {
      if (Array.isArray(item) && depth > 0) {
        acc.push(...this.flatten(item, depth - 1));
      } else {
        acc.push(item);
      }
      return acc;
    }, []);
  }

  /**
   * Capitalize first letter of string
   * @param {string} str - String to capitalize
   * @returns {string} Capitalized string
   */
  static capitalize(str) {
    if (typeof str !== 'string' || str.length === 0) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Convert string to camelCase
   * @param {string} str - String to convert
   * @returns {string} CamelCase string
   */
  static camelCase(str) {
    if (typeof str !== 'string') return str;
    
    return str
      .replace(/[_\-\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : '')
      .replace(/^./, c => c.toLowerCase());
  }

  /**
   * Convert string to kebab-case
   * @param {string} str - String to convert
   * @returns {string} Kebab-case string
   */
  static kebabCase(str) {
    if (typeof str !== 'string') return str;
    
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
  }

  /**
   * Convert string to snake_case
   * @param {string} str - String to convert
   * @returns {string} Snake_case string
   */
  static snakeCase(str) {
    if (typeof str !== 'string') return str;
    
    return str
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .replace(/[\s\-]+/g, '_')
      .toLowerCase();
  }

  /**
   * Truncate string to specified length
   * @param {string} str - String to truncate
   * @param {number} length - Maximum length
   * @param {string} suffix - Suffix to append (default: '...')
   * @returns {string} Truncated string
   */
  static truncate(str, length, suffix = '...') {
    if (typeof str !== 'string') return str;
    if (str.length <= length) return str;
    return str.slice(0, length - suffix.length) + suffix;
  }

  /**
   * Remove HTML tags from string
   * @param {string} str - String to clean
   * @returns {string} Cleaned string
   */
  static stripHtml(str) {
    if (typeof str !== 'string') return str;
    return str.replace(/<[^>]*>/g, '');
  }

  /**
   * Escape HTML special characters
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  static escapeHtml(str) {
    if (typeof str !== 'string') return str;
    
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    
    return str.replace(/[&<>"']/g, m => map[m]);
  }

  /**
   * Unescape HTML entities
   * @param {string} str - String to unescape
   * @returns {string} Unescaped string
   */
  static unescapeHtml(str) {
    if (typeof str !== 'string') return str;
    
    const map = {
      '&amp;': '&',
      '&lt;': '<',
      '&gt;': '>',
      '&quot;': '"',
      '&#039;': "'"
    };
    
    return str.replace(/&amp;|&lt;|&gt;|&quot;|&#039;/g, m => map[m]);
  }

  /**
   * Detect browser name
   * @returns {string} Browser name
   */
  static detectBrowser() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return 'unknown';
    }

    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      return 'chrome';
    }
    if (userAgent.includes('firefox')) {
      return 'firefox';
    }
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'safari';
    }
    if (userAgent.includes('edg')) {
      return 'edge';
    }
    if (userAgent.includes('opera') || userAgent.includes('opr')) {
      return 'opera';
    }

    return 'unknown';
  }

  /**
   * Detect device type
   * @returns {string} Device type ('mobile', 'tablet', 'desktop')
   */
  static detectDevice() {
    if (typeof window === 'undefined') {
      return 'unknown';
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const width = window.innerWidth;

    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
      return 'mobile';
    }
    if (/tablet|ipad|playbook|silk/i.test(userAgent) || (width >= 768 && width < 1024)) {
      return 'tablet';
    }
    return 'desktop';
  }

  /**
   * Check if feature is supported
   * @param {string} feature - Feature name (e.g., 'localStorage', 'sessionStorage')
   * @returns {boolean} True if feature is supported
   */
  static isFeatureSupported(feature) {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      switch (feature) {
        case 'localStorage':
          return typeof Storage !== 'undefined' && 'localStorage' in window;
        case 'sessionStorage':
          return typeof Storage !== 'undefined' && 'sessionStorage' in window;
        case 'geolocation':
          return 'geolocation' in navigator;
        case 'serviceWorker':
          return 'serviceWorker' in navigator;
        case 'webSocket':
          return 'WebSocket' in window;
        case 'fetch':
          return typeof fetch !== 'undefined';
        case 'promises':
          return typeof Promise !== 'undefined';
        case 'async':
          return typeof async !== 'undefined';
        default:
          return feature in window || feature in navigator;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Copy text to clipboard
   * @param {string} text - Text to copy
   * @returns {Promise<boolean>} True if successful
   */
  static async copyToClipboard(text) {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Measure function execution time
   * @param {Function} func - Function to measure
   * @param {...any} args - Function arguments
   * @returns {Object} Result with execution time and return value
   */
  static measureTime(func, ...args) {
    const start = performance.now();
    const result = func.apply(this, args);
    const end = performance.now();
    const duration = end - start;

    return {
      result,
      duration,
      durationMs: duration,
      durationS: duration / 1000
    };
  }

  /**
   * Measure async function execution time
   * @param {Function} asyncFunc - Async function to measure
   * @param {...any} args - Function arguments
   * @returns {Promise<Object>} Result with execution time and return value
   */
  static async measureTimeAsync(asyncFunc, ...args) {
    const start = performance.now();
    const result = await asyncFunc.apply(this, args);
    const end = performance.now();
    const duration = end - start;

    return {
      result,
      duration,
      durationMs: duration,
      durationS: duration / 1000
    };
  }

  /**
   * Create performance monitor
   * @param {string} label - Performance label
   * @returns {Object} Performance monitor with start and end methods
   */
  static createPerformanceMonitor(label) {
    const startTime = performance.now();
    
    return {
      label,
      start: () => performance.now(),
      end: () => {
        const endTime = performance.now();
        const duration = endTime - startTime;
        
        if (typeof console !== 'undefined' && console.log) {
          console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
        }
        
        return {
          label,
          duration,
          durationMs: duration,
          durationS: duration / 1000
        };
      },
      mark: (name) => {
        if (performance.mark) {
          performance.mark(`${label}-${name}`);
        }
      },
      measure: (startMark, endMark, measureName) => {
        if (performance.measure) {
          const measureLabel = measureName || `${label}-${startMark}-to-${endMark}`;
          try {
            performance.measure(measureLabel, `${label}-${startMark}`, `${label}-${endMark}`);
            return performance.getEntriesByName(measureLabel)[0];
          } catch (error) {
            return null;
          }
        }
        return null;
      }
    };
  }

  /**
   * Sleep/delay function
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} Promise that resolves after specified time
   */
  static sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Retry function with exponential backoff
   * @param {Function} func - Function to retry
   * @param {number} maxAttempts - Maximum retry attempts
   * @param {number} baseDelay - Base delay in milliseconds
   * @returns {Promise} Promise that resolves when function succeeds or max attempts reached
   */
  static async retryWithBackoff(func, maxAttempts = 3, baseDelay = 1000) {
    let lastError;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        return await func();
      } catch (error) {
        lastError = error;
        
        if (attempt < maxAttempts - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          await this.sleep(delay);
        }
      }
    }

    throw lastError;
  }

  /**
   * Check if value is empty
   * @param {any} value - Value to check
   * @returns {boolean} True if empty
   */
  static isEmpty(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  }

  /**
   * Check if value is not empty
   * @param {any} value - Value to check
   * @returns {boolean} True if not empty
   */
  static isNotEmpty(value) {
    return !this.isEmpty(value);
  }

  /**
   * Generate unique ID
   * @param {string} prefix - Optional prefix
   * @returns {string} Unique ID
   */
  static generateId(prefix = '') {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 9);
    return prefix ? `${prefix}-${timestamp}-${random}` : `${timestamp}-${random}`;
  }

  /**
   * Format bytes to human-readable size
   * @param {number} bytes - Bytes to format
   * @param {number} decimals - Decimal places
   * @returns {string} Formatted size string
   */
  static formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}

// Export the Helpers class
export default Helpers;

